/**
 * Product category catalog — single source for ProductCategories UI, routing, and SEO.
 * Copy targets heavy earth-moving machinery (excavators, bulldozers, loaders, graders, ADTs).
 *
 * Two axes of categorisation are supported:
 *   kind: 'system'  — function-based (Engine, Hydraulic, GET, …). Browse by what's broken.
 *   kind: 'brand'   — machine/OEM-based (LiuGong, SDLG, XCMG, ZF). Browse by what you own.
 *
 * Brand categories drive the per-item catalog defined in `productItems.js` and
 * generate individual SEO-optimised product detail pages.
 */

export const SITE_BASE_URL = 'https://tradezoneequipments.com';

export const PRODUCT_CATEGORIES = [
  {
    slug: 'engine-parts',
    kind: 'system',
    title: 'Engine Parts',
    shortDescription:
      'Cylinder heads, gaskets, turbochargers, and overhaul kits for diesel power units on crawler and wheeled earth movers.',
    seoTitle:
      'Heavy Equipment Engine Parts | Excavator & Bulldozer Diesel Spares | Trade Zone Equipments',
    seoDescription:
      'Source OEM-grade engine parts for excavators, bulldozers, wheel loaders, and motor graders. Gaskets, turbos, injectors, and rebuild components with expert fitment support from Trade Zone Equipments.',
    keywords:
      'excavator engine parts, bulldozer diesel spares, wheel loader engine kit, motor grader engine overhaul, heavy equipment gasket, turbocharger replacement earthmoving',
    color: '#ff6b35',
    iconName: 'Cog',
    intro:
      'Keep diesel power plants reliable under load with components matched to harsh digging, pushing, and hauling cycles. We support common OEM platforms used on mines, infrastructure, and quarry fleets.',
    applications: [
      'Hydraulic excavators and mass excavators',
      'Track-type tractors and dozers',
      'Wheel loaders and tool carriers',
      'Motor graders and scrapers',
    ],
  },
  {
    slug: 'hydraulic-components',
    kind: 'system',
    title: 'Hydraulic Components',
    shortDescription:
      'Pumps, motors, valves, cylinders, and seals that maintain breakout force and cycle times on modern earth-moving hydraulics.',
    seoTitle:
      'Earthmoving Hydraulic Parts | Pumps, Valves & Cylinders | Trade Zone Equipments',
    seoDescription:
      'Hydraulic pumps, swing and travel motors, control valves, and boom or bucket cylinders for excavators, loaders, and dozers. Parts selected for pressure stability and long seal life in abrasive job sites.',
    keywords:
      'excavator hydraulic pump, loader hydraulic cylinder, dozer valve block, swing motor spare parts, earthmoving hydraulic seal kit',
    color: '#ff8c42',
    iconName: 'Droplet',
    intro:
      'Hydraulic performance defines productivity on site. Our line focuses on components that restore flow, reduce internal leakage, and protect your implement control under continuous duty.',
    applications: [
      'Load-sensing and open-center excavator systems',
      'Loader lift and tilt circuits',
      'Dozer blade and ripper hydraulics',
      'Grader circle and moldboard controls',
    ],
  },
  {
    slug: 'transmission-drivetrain',
    kind: 'system',
    title: 'Transmission & Drivetrain',
    shortDescription:
      'Torque converters, transmissions, differentials, and driveline hardware for smooth power transfer on heavy movers.',
    seoTitle:
      'Heavy Equipment Transmission Parts | Torque Converter & Driveline | Trade Zone Equipments',
    seoDescription:
      'Transmission spares, torque converters, axles, and differentials for articulated dump trucks, loaders, and graders. Components aimed at reducing slippage, heat, and downtime in high-traction applications.',
    keywords:
      'wheel loader transmission parts, ADT differential, motor grader driveline, torque converter rebuild kit, earthmoving transmission spares',
    color: '#ffa500',
    iconName: 'Settings',
    intro:
      'Drivetrain faults stop revenue fast. We stock and source hard parts that restore shift quality, reduce parasitic loss, and extend service intervals on mixed fleets.',
    applications: [
      'Powershift and hydrostatic wheel loaders',
      'Articulated dump truck bogies',
      'All-wheel-drive graders',
      'Large crawler carriers',
    ],
  },
  {
    slug: 'undercarriage-parts',
    kind: 'system',
    title: 'Undercarriage Parts',
    shortDescription:
      'Track groups, shoes, rollers, idlers, and sprockets engineered for high impact and abrasive underfoot conditions.',
    seoTitle:
      'Excavator & Dozer Undercarriage Parts | Track, Rollers, Idlers | Trade Zone Equipments',
    seoDescription:
      'Complete undercarriage solutions for excavators, dozers, and track loaders—track chains, shoes, carrier rollers, track rollers, idlers, and sprockets focused on even wear and longer hours between turns.',
    keywords:
      'excavator track chain, dozer undercarriage kit, track roller replacement, sprocket segment earthmoving, crawler carrier roller',
    color: '#ff6b35',
    iconName: 'Link2',
    intro:
      'Undercarriage is the largest wear pool on tracked machines. We help you spec the right balance of abrasion resistance, impact toughness, and cost per hour for your soil conditions.',
    applications: [
      'Mass excavation and trenching crawlers',
      'Mine and quarry dozers',
      'Pipeline and landfill track machines',
      'Forestry and steep-slope carriers',
    ],
  },
  {
    slug: 'electrical-systems',
    kind: 'system',
    title: 'Electrical & Sensors',
    shortDescription:
      'Alternators, starters, harnesses, switches, and sensors that keep ECM-driven earth movers communicating and protected.',
    seoTitle:
      'Heavy Equipment Electrical Parts | ECM Sensors & Lighting | Trade Zone Equipments',
    seoDescription:
      'Electrical components for modern earth-moving fleets—charging systems, starting motors, relays, sensors, and safety lighting. Ideal for machines running telematics, emissions controls, and advanced hydraulics.',
    keywords:
      'excavator alternator, loader starter motor, earthmoving sensor harness, ECM wiring heavy equipment, LED beacon mining machine',
    color: '#ff8c42',
    iconName: 'Zap',
    intro:
      'Voltage drops and false sensor readings create ghost faults. We focus on parts with stable thermal performance and connector integrity suited to vibration-rich environments.',
    applications: [
      'Tier 4 and Stage V diesel platforms',
      'GPS and machine control ready fleets',
      'Night-shift excavation and hauling',
      'Cold-climate starting support',
    ],
  },
  {
    slug: 'wear-parts-get',
    kind: 'system',
    title: 'Wear Parts & GET',
    shortDescription:
      'Bucket teeth, edges, adapters, and wear packages that protect buckets, blades, and rippers in high abrasion soils.',
    seoTitle:
      'Ground Engaging Tools | Bucket Teeth & Cutting Edges | Trade Zone Equipments',
    seoDescription:
      'GET for excavators, loaders, and dozers—adapters, teeth, shrouds, half arrows, and cutting edges designed to improve penetration, carry capacity, and change-out speed on production earth-moving sites.',
    keywords:
      'excavator bucket teeth, loader cutting edge, dozer blade wear parts, ripper shank protector, GET mining excavator',
    color: '#ffa500',
    iconName: 'Wrench',
    intro:
      'The right GET lowers fuel per bank cubic meter and protects the shell of the bucket or blade. We help you match material profiles—from blasted rock to overburden clay.',
    applications: [
      'Quarry and shot-rock loading',
      'Mass earthworks and cut-to-fill',
      'Coal and lightweight bulk materials',
      'Road building and subgrade preparation',
    ],
  },
  {
    slug: 'filters-fluids-maintenance',
    kind: 'system',
    title: 'Filters & Maintenance',
    shortDescription:
      'High-efficiency filters, breathers, and maintenance kits aligned to OEM service intervals for earth-moving fleets.',
    seoTitle:
      'Heavy Equipment Filters | Hydraulic, Fuel & Air Kits | Trade Zone Equipments',
    seoDescription:
      'Spin-on and cartridge filters for fuel, hydraulic, engine oil, air, and cabin circuits on excavators, loaders, and ADTs. Bundled maintenance kits simplify PMs and protect warranties.',
    keywords:
      'excavator hydraulic filter, loader fuel water separator, bulldozer air filter kit, earthmoving PM kit, cabin HVAC filter heavy equipment',
    color: '#ff6b35',
    iconName: 'Filter',
    intro:
      'Clean fluids extend component life. Our filter assortment targets high dust, high soot, and high water risk environments typical of earth-moving operations.',
    applications: [
      'Desert and high-dust crushing plants',
      'Wet and tropical fuel quality challenges',
      'Cold-start wax control programs',
      'Fleet PM standardization',
    ],
  },
  {
    slug: 'cooling-radiator',
    kind: 'system',
    title: 'Cooling & Radiator',
    shortDescription:
      'Radiators, charge air coolers, water pumps, and thermostats that stabilize temperatures under heavy dig cycles.',
    seoTitle:
      'Excavator Cooling System Parts | Radiator & Aftercooler | Trade Zone Equipments',
    seoDescription:
      'Cooling modules, radiators, intercoolers, fans, and water pumps for loaders, excavators, and haul trucks working in high ambient or slow-speed high-load conditions.',
    keywords:
      'excavator radiator replacement, wheel loader charge air cooler, dozer water pump, heavy equipment fan clutch, earthmoving cooling stack',
    color: '#ff8c42',
    iconName: 'Fan',
    intro:
      'Heat management prevents derates and protects emissions hardware. We supply cooling path components that restore airflow, reduce fin fouling sensitivity, and stabilize jacket temperatures.',
    applications: [
      'Tropical and high-altitude sites',
      'Continuous tramming and load-and-carry',
      'Ripper and push dozer duty',
      'Tunneling and confined ventilation',
    ],
  },
  {
    slug: 'fuel-injection',
    kind: 'system',
    title: 'Fuel & Injection',
    shortDescription:
      'Injection pumps, injectors, lines, and filtration tuned for low-sulfur fuels and modern emissions aftertreatment.',
    seoTitle:
      'Heavy Equipment Fuel Injection Parts | Injectors & Pumps | Trade Zone Equipments',
    seoDescription:
      'Fuel injection hardware for earth movers—common rail injectors, supply pumps, high-pressure lines, and dosing modules aimed at clean combustion and reliable cold starts.',
    keywords:
      'excavator injector replacement, common rail pump loader, fuel transfer pump bulldozer, earthmoving DEF dosing valve',
    color: '#ffa500',
    iconName: 'Fuel',
    intro:
      'Fuel quality varies by region; precision matters. Our injection catalog emphasizes leak-free sealing, correct spray patterns, and compatibility with onboard diagnostics.',
    applications: [
      'Common rail diesel fleets',
      'Bio-diesel blended operations',
      'High-hour rental machines',
      'Low idle urban demolition fleets',
    ],
  },
  {
    slug: 'brakes-steering',
    kind: 'system',
    title: 'Brakes & Steering',
    shortDescription:
      'Service brakes, parking systems, orbitrols, cylinders, and pins that keep wheeled earth movers stable and predictable.',
    seoTitle:
      'Wheel Loader Brakes & Steering Parts | Calipers & Orbitrol | Trade Zone Equipments',
    seoDescription:
      'Brake discs, calipers, pads, valves, steering cylinders, and orbitrol units for wheel loaders, ADTs, and motor graders. Focused on safe stopping on grade and precise blade or bucket placement.',
    keywords:
      'wheel loader brake caliper, ADT parking brake, motor grader orbitrol, steering cylinder earthmoving, wet brake parts loader',
    color: '#ff6b35',
    iconName: 'Gauge',
    intro:
      'Steering and braking faults are safety critical. We supply parts that restore pedal feel, parking hold, and hydraulic steering authority for mixed-surface job sites.',
    applications: [
      'Steep quarry haul roads',
      'Tight urban infrastructure lifts',
      'Snow and low-mu surfaces',
      'Fine grading finish passes',
    ],
  },
  {
    slug: 'attachments-implements',
    kind: 'system',
    title: 'Attachments & Implements',
    shortDescription:
      'Quick couplers, thumbs, rippers, forks, and specialty buckets that adapt machines to changing production tasks.',
    seoTitle:
      'Excavator & Loader Attachments | Quick Couplers & Buckets | Trade Zone Equipments',
    seoDescription:
      'Attachment parts and wear items for excavators and loaders—coupler pins, safety locks, thumbs, rippers, and material-specific buckets to improve versatility without sacrificing breakout force.',
    keywords:
      'excavator quick coupler parts, loader fork carriage, hydraulic thumb cylinder, ripper shank earthmoving, trenching bucket spares',
    color: '#ff8c42',
    iconName: 'Construction',
    intro:
      'Attachments turn a single carrier into many tools. We support pins, bushings, hoses, and guarding that keep couplers safe and implements aligned under side load.',
    applications: [
      'Utility trenching and lifting',
      'Demolition sorting and loading',
      'Pipe laying and bedding',
      'Stockpile rehandling',
    ],
  },
  {
    slug: 'cab-operator-station',
    kind: 'system',
    title: 'Cab & Operator Station',
    shortDescription:
      'Glass, seals, seats, pedals, joysticks, and HVAC parts that improve visibility, comfort, and control ergonomics.',
    seoTitle:
      'Heavy Equipment Cab Parts | Glass, HVAC & Controls | Trade Zone Equipments',
    seoDescription:
      'Operator station components for earth movers—cab glass, door seals, suspension seats, joysticks, pedals, and HVAC cores built for long shifts in dust, noise, and vibration.',
    keywords:
      'excavator cab glass, loader HVAC blower, motor grader joystick, earthmoving suspension seat, door seal crawler machine',
    color: '#ffa500',
    iconName: 'PanelTop',
    intro:
      'Operator fatigue affects productivity and safety. Our cab-focused line restores sealing, climate control, and precise machine handling for day and night crews.',
    applications: [
      'Dust-controlled demolition zones',
      'Extreme heat or cold climates',
      'Remote fly-in fly-out projects',
      'Training and dual-control setups',
    ],
  },

  // -------------------------------------------------------------------------
  // Brand-based categories. Each brand drives a per-item catalog defined in
  // `productItems.js` and exposes individual SEO-optimised product pages.
  // -------------------------------------------------------------------------
  {
    slug: 'liugong-wheel-loader',
    kind: 'brand',
    brandLabel: 'LiuGong',
    title: 'LiuGong Wheel Loader Parts',
    shortDescription:
      'Genuine-fit pumps, gears, valves, and drivetrain spares for LiuGong CLG-series wheel loaders.',
    seoTitle:
      'LiuGong Wheel Loader Parts | CLG855N CLG856H Spares | Trade Zone Equipments',
    seoDescription:
      'OEM-grade LiuGong wheel loader spare parts — water pumps, oil pumps, gear pumps, HV pumps, steering pumps, planetary gears, and drivetrain components for CLG835, CLG842, CLG855N, CLG856H, and CLG862H machines.',
    keywords:
      'LiuGong wheel loader parts, CLG855N spare parts, CLG856H parts, LiuGong water pump, LiuGong hydraulic pump, LiuGong planetary gear, LiuGong steering pump',
    color: '#ffb300',
    iconName: 'Truck',
    intro:
      'LiuGong CLG-series wheel loaders are workhorses across mining, construction, and quarry sites. We supply pump-set rebuild parts, drivetrain hardware, and hydraulic spares matched to OEM specifications — keeping cycle times and breakout force where they should be.',
    applications: [
      'CLG835 / CLG842 compact wheel loaders',
      'CLG855N / CLG856H production loaders',
      'CLG862H large wheel loaders',
      'Quarry, mining, and bulk-handling fleets',
    ],
  },
  {
    slug: 'sdlg-wheel-loader',
    kind: 'brand',
    brandLabel: 'SDLG',
    title: 'SDLG Wheel Loader Parts',
    shortDescription:
      'Hydraulic, drivetrain, brake, and cab parts for SDLG LG-series wheel loaders.',
    seoTitle:
      'SDLG Wheel Loader Parts | LG956L LG958L Spares | Trade Zone Equipments',
    seoDescription:
      'SDLG wheel loader spare parts including working pumps, steering pumps, brake discs, hydraulic cylinders, control valves, and cabin components for LG936L, LG946L, LG953, LG956L, and LG958L machines.',
    keywords:
      'SDLG wheel loader parts, LG956L spare parts, LG958L parts, SDLG working pump, SDLG brake disc, SDLG hydraulic cylinder, SDLG steering pump',
    color: '#ff8c42',
    iconName: 'Truck',
    intro:
      'SDLG wheel loaders are valued for low total cost of ownership. We support the platform with hydraulic, brake, and drivetrain parts that restore production rates without overspending on imported OEM where a fitted alternative will do.',
    applications: [
      'LG936L / LG946L compact loaders',
      'LG953 / LG956L production loaders',
      'LG958L large wheel loaders',
      'Yard, civil, and aggregate handling',
    ],
  },
  {
    slug: 'xcmg-wheel-loader',
    kind: 'brand',
    brandLabel: 'XCMG',
    title: 'XCMG Wheel Loader Parts',
    shortDescription:
      'Pumps, control valves, and drivetrain parts for XCMG LW and ZL-series wheel loaders.',
    seoTitle:
      'XCMG Wheel Loader Parts | LW500FN ZL50GN Spares | Trade Zone Equipments',
    seoDescription:
      'XCMG wheel loader spare parts — charging pumps, steering pumps, working pumps, multi-way control valves, and hydraulic spares for LW300FN, LW500FN, and ZL50GN machines.',
    keywords:
      'XCMG wheel loader parts, LW500FN spare parts, ZL50GN parts, XCMG charging pump, XCMG steering pump, XCMG working pump, XCMG control valve',
    color: '#ffa500',
    iconName: 'Truck',
    intro:
      'XCMG LW and ZL-series wheel loaders see heavy use on infrastructure and aggregate sites. We focus on pump-set, hydraulic, and drivetrain parts that restore lift, tilt, and travel performance after high-hour wear.',
    applications: [
      'LW300FN compact loaders',
      'LW500FN production loaders',
      'ZL50GN heavy-duty loaders',
      'Infrastructure, road, and aggregate sites',
    ],
  },
  {
    slug: 'zf-transmission-parts',
    kind: 'brand',
    brandLabel: 'ZF',
    title: 'ZF Transmission Parts',
    shortDescription:
      'Clutch discs, seal kits, and service piston kits for ZF powershift transmissions in wheel loaders and ADTs.',
    seoTitle:
      'ZF Transmission Parts | 4WG200 6WG200 Clutch Discs & Seal Kits | Trade Zone Equipments',
    seoDescription:
      'ZF powershift transmission spare parts — friction clutch discs, complete seal kits, and lift/piston kits for 4WG180, 4WG200, and 6WG200 series transmissions fitted to wheel loaders, motor graders, and articulated dump trucks.',
    keywords:
      'ZF transmission parts, ZF 4WG200 clutch disc, ZF 6WG200 friction plate, ZF 4WG180 spare parts, ZF transmission seal kit, ZF powershift overhaul',
    color: '#ff6b35',
    iconName: 'GitMerge',
    intro:
      'ZF powershift transmissions are the backbone of wheel loader and grader drivetrains. We supply friction packs, seal kits, and service-piston kits with the documentation and quality control required for confident rebuilds.',
    applications: [
      'ZF 4WG180 powershift transmissions',
      'ZF 4WG200 powershift transmissions',
      'ZF 6WG200 powershift transmissions',
      'Wheel loader, grader, and ADT rebuilds',
    ],
  },
];

export function getCategoryBySlug(slug) {
  return PRODUCT_CATEGORIES.find((c) => c.slug === slug) ?? null;
}

export function categoryPath(slug) {
  return `/category/${slug}`;
}

/**
 * System categories — function-based browse axis (engine, hydraulic, GET, …).
 */
export function getSystemCategories() {
  return PRODUCT_CATEGORIES.filter((c) => c.kind === 'system');
}

/**
 * Brand categories — machine/OEM-based browse axis (LiuGong, SDLG, XCMG, ZF).
 */
export function getBrandCategories() {
  return PRODUCT_CATEGORIES.filter((c) => c.kind === 'brand');
}
